import { useEffect, useMemo, useState } from "react"
import { Form, FormField, option, FieldType, RadioField, O, ModalId, useUIActions, FieldValue } from "../../../../../features/ui"
import { Lot, LotAttributes, NO_LOT, useLotLoaders, useProductLoaders } from "../../../../products"
import { TableName } from "../../../../../tables"
import { firstItem } from "../../../../../utils/listUtils"
import { LotAttributeTable } from "./lotAttributeTable"
import { DeliveryItemLotData, DeliveryItemRow } from "../../types"

type Props = {
    currentItem?: DeliveryItemRow
    onSubmit: (data: DeliveryItemLotData) => void
}

export const DefineLotForm = (props: Props) => {
    const { currentItem, onSubmit } = props

    const createOption = useMemo(() => option('create-new', 'Crear nueva partida'), [])
    const selectOption = useMemo(() => option('select-existing', 'Seleccionar partida existente'), [])
    const noLotOption = useMemo(() => option('no-lot', NO_LOT), [])
    const choiceOptions = useMemo(() => [createOption, selectOption, noLotOption], [])

    const [lots, setLots] = useState<Lot[]>([])
    const [baseLot, setBaseLot] = useState<Lot>()
    const [choice, setChoice] = useState<O<string>>(noLotOption.value)
    const [lotCode, setLotCode] = useState<string>()
    const [lotAttributes, setLotAttributes] = useState<LotAttributes>()
    const [lotId, setLotId] = useState<string>()

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const { closeModal } = useUIActions()

    const nonBaseLots = (lotsParam: Lot[], baseLotParam?: Lot) => lotsParam.filter(lot => lot.id !== baseLotParam?.id)

    const load = async () => {
        if (currentItem) {
            const { loadProduct } = productLoaders()
            const loadedProuct = await loadProduct(currentItem.productId)
            const { loadProductLots } = lotLoaders()
            const lots = await loadProductLots(loadedProuct!.id)
            setLots(lots)
            const baseLot = lots.find(lot => lot.code === NO_LOT)!
            setBaseLot(baseLot)        
            const { lotCode, lotAttributes, lotId } = currentItem.lotData || {}
            const currentChoice = lotCode || !lotId ? createOption.value : (lotId === baseLot.id ? noLotOption.value : selectOption.value)
            setChoice(currentChoice)
            if (currentChoice === createOption.value) {
                setLotCode(lotCode)
                setLotAttributes(lotAttributes)
            } else {
                setLotId(lotId || firstItem(nonBaseLots(lots, baseLot))?.id)
            }
        }
    }

    useEffect(() => {
        load()
    }, [currentItem])
    
    const lotOptions = useMemo(() => nonBaseLots(lots, baseLot).map(lot => option(lot.id, lot.code)), [lots, baseLot])

    const onChangeChoice = (value?: FieldValue) => {
        const newChoice = value as O<string>
        setChoice(newChoice)
        if (newChoice === createOption.value) {
            setLotId(undefined)
        } else if (newChoice === selectOption.value) {
            setLotId(firstItem(lotOptions)?.value)
            setLotCode(undefined)
            setLotAttributes(undefined)
        } else if (newChoice === noLotOption.value) {
            setLotId(baseLot?.id)
            setLotCode(undefined)
            setLotAttributes(undefined)
        }
    }

    const onChangeLotCode = (value?: FieldValue) => setLotCode(value as string)

    const onSelectLot = (value?: FieldValue) => setLotId(value as O<string>)
        
    const fields: FormField[] = [
        {
            name: 'choice',
            type: FieldType.SELECT,
            label: 'Partida',
            render: () => <RadioField
                id='choice'
                value={choice}
                options={choiceOptions}
                disabledOptions={lotOptions.length === 0 ? [selectOption.value] : []}
                onChange={onChangeChoice}
            />
        }
    ]

    if (choice === createOption.value) {
        const codeField: FormField = {
            name: 'lotCode',
            type: FieldType.TEXT,
            label: 'Código',
            value: lotCode,
            onChange: onChangeLotCode
        }
        const attributesField: FormField = {
            name: 'attributes',
            type: FieldType.TABLE,
            label: 'Atributos',
            hint: 'Definir tono, calibre y/o fecha de vencimiento.',
            render: () => (
                <LotAttributeTable
                    value={lotAttributes}
                    onChange={setLotAttributes}
                />
            )
        }
        fields.push(codeField, attributesField)
    } else if (choice === selectOption.value && lots.length > 0) {
        const lotField: FormField = {
            name: 'lotId',
            type: FieldType.TABLE,
            table: TableName.LOTS,
            value: lotId,
            options: lotOptions,
            label: 'Código',
            onChange: onSelectLot
        }
        fields.push(lotField)
    }

    const handleSubmit = () => {
        closeModal(ModalId.DEFINE_LOT_FORM)
        onSubmit({
            lotCode,
            lotId,
            lotAttributes
        })
    }

    const onCancel = () => {
        closeModal(ModalId.DEFINE_LOT_FORM)
    }

    return (
        <Form
            formId="define-lot-form"
            fields={fields}
            createMode={false}
            onSubmit={handleSubmit}
            onCancel={onCancel}
        />
    )
}
